import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import {
  Box,
  Typography,
  Logo,
  Avatar,
  Menu,
  MenuItem,
  Separator,
  portalChrome,
} from '@glplus/glpweb-dsl';
import { TOP_NAV_TOOLS } from '../lib/adminTools';
import { useCapabilities } from '../lib/useCapabilities';

/**
 * Vendor Admin header — built the same way as the vendor portal's MainHeader:
 * DSL primitives + portalChrome, no MUI / raw HTML. Admin chrome is black with
 * the "mixed" logo (colored G+ + light wordmark) per `portalChrome.admin`.
 *
 * Layout mirrors the Figma: logo + "Vendor Admin" on the left, the primary top-nav
 * and the account avatar menu on the right.
 */

const CHROME = portalChrome.admin;
const LOGIN_URL = import.meta.env.VITE_LOGIN_URL as string;

interface NavLink {
  label: string;
  to: string;
}

export default function MainHeader() {
  const { pathname } = useLocation();
  const caps = useCapabilities();
  const email = caps.data?.email ?? '';
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Dashboard is always shown; each top-nav tool appears only if the caller's
  // per-app access grants its capability (server re-checks every request).
  const NAV: NavLink[] = [
    { label: 'Dashboard', to: '/' },
    ...TOP_NAV_TOOLS.filter((t) => caps.has(t.capability)).map((t) => ({
      label: t.navLabel ?? t.label,
      to: t.to as string,
    })),
  ];

  const onSignOut = async () => {
    setMenuAnchor(null);
    try {
      await signOut();
    } finally {
      window.location.assign(LOGIN_URL);
    }
  };

  const isActive = (to: string) =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

  return (
    <Box
      component="header"
      sx={{
        background: { xs: CHROME.headerBgMobile, md: CHROME.headerBg },
        color: '#fff',
        minHeight: 64,
        px: { xs: '16px', sm: '32px', lg: '40px' },
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      {/* Logo + portal label */}
      <Box
        component={Link}
        to="/"
        aria-label="Vendor Admin home"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          textDecoration: 'none',
          '& img': {
            width: '100% !important',
            maxWidth: { xs: '150px', md: '200px' },
            display: 'block',
          },
        }}
      >
        <Logo logoStyle="full" size="desktop" color={CHROME.logoVariant} />
        <Typography
          sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '15px', letterSpacing: 0.5 }}
        >
          Vendor Admin
        </Typography>
      </Box>

      {/* Right cluster: top-nav + account */}
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
        <Box
          component="nav"
          aria-label="Vendor Admin navigation"
          sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}
        >
          {NAV.map((item) => {
            const active = isActive(item.to);
            return (
              <Typography
                key={item.to}
                component={Link}
                to={item.to}
                sx={{
                  color: active ? CHROME.navActive : CHROME.navInactive,
                  textDecoration: 'none',
                  fontWeight: active ? 700 : 600,
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  borderBottom: active ? '2px solid #fff' : '2px solid transparent',
                  py: 0.5,
                  '&:hover': { color: CHROME.navActive },
                }}
              >
                {item.label}
              </Typography>
            );
          })}
        </Box>

        <Box
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          role="button"
          aria-haspopup="menu"
          aria-expanded={!!menuAnchor}
          aria-label="Account menu"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setMenuAnchor(e.currentTarget);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            borderRadius: 1,
            p: 0.5,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
            '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
          }}
        >
          <Avatar ariaLabel="Account menu" fallbackText={email || undefined} bgColor="#ffffff" size="small" />
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 240 } } }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Avatar ariaLabel={email} fallbackText={email || undefined} bgColor="#343F5B" size="medium" />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>Vendor Admin</Typography>
              <Typography
                variant="caption"
                sx={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
              >
                {email}
              </Typography>
            </Box>
          </Box>
          <Separator />
          <MenuItem icon="logout" onClick={() => { void onSignOut(); }}>
            Sign out
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
